import { localDateKey } from "@/lib/clinical/psylogNative";
import { assessVitalRecord, normalizeVitalRecord } from "@/lib/clinical/vitalAssessment";

export type PsyLogEventType = "dose" | "symptom" | "checkin" | "session" | "substance_use" | "psylog" | "vital";

export type PsyLogTimelineEvent = {
  id: string;
  type: PsyLogEventType;
  at: string;
  title: string;
  detail: string;
  intensity?: number | null;
  source: any;
  linkedDoseId?: string | null;
  tags: string[];
};

function validIso(value: any): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function text(values: any[]) {
  return values.filter((value) => value !== null && value !== undefined && value !== "").join(" · ");
}

function numberValue(value: any): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function canonical(value: any) {
  return String(value ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "");
}

function doseEvent(row: any): PsyLogTimelineEvent | null {
  const at = validIso(row.actual_time);
  if (!at) return null;
  const dose = row.dose_text || text([row.dose_amount, row.dose_unit]);
  const context = text([
    row.log_type,
    row.taken_with_food === true ? "com alimento" : row.taken_with_food === false ? "sem alimento" : null,
    row.stomach ? `estômago ${row.stomach}` : null,
    row.caffeine_near_dose_mg != null ? `cafeína próxima ${row.caffeine_near_dose_mg} mg` : null,
    row.sleep_deprivation_at_dose_0_10 != null ? `privação ${row.sleep_deprivation_at_dose_0_10}/10` : null,
    row.perceived_effect_text,
    row.beverage_name ? `bebida ${row.beverage_name}` : null,
    row.ethanol_grams != null ? `etanol ${Number(row.ethanol_grams).toFixed(2)} g` : null,
    row.powder_mass_mg != null ? `material ${row.powder_mass_mg} mg` : null,
    row.purity_status === "unknown" ? "pureza desconhecida" : null,
    row.purity_percent != null ? `pureza ${row.purity_percent}%` : null,
    row.estimated_active_mg != null ? `massa ativa estimada ${Number(row.estimated_active_mg).toFixed(1)} mg` : null,
    row.material_profile_snapshot?.profile_name ? `perfil/amostra ${row.material_profile_snapshot.profile_name}` : null,
    row.material_profile_snapshot?.composition_status ? `composição ${row.material_profile_snapshot.composition_status} · confiança ${row.material_profile_snapshot.composition_confidence ?? "não informada"}` : null,
    row.material_profile_risk_interpretation?.specificRisks?.length ? `riscos específicos: ${row.material_profile_risk_interpretation.specificRisks.join("; ")}` : null,
    row.material_profile_risk_interpretation?.limitations?.length ? `limitações: ${row.material_profile_risk_interpretation.limitations.slice(0, 2).join("; ")}` : null,
    row.purity_source ? `origem pureza: ${row.purity_source}` : null,
    row.notes_patient ? `relato do paciente: ${row.notes_patient}` : null,
    row.notes_clinician ? `observação médica: ${row.notes_clinician}` : null,
  ]);
  return {
    id: `dose:${row.id}`,
    type: "dose",
    at,
    title: row.substance_name || row.substances?.name || row.free_text_name || "Dose",
    detail: text([dose, row.substance_formulations?.formulation_name || row.formulation_name, context]),
    intensity: numberValue(row.benefit_0_100 ?? row.focus_0_100),
    source: row,
    linkedDoseId: row.id,
    tags: [row.log_type, row.substances?.name, row.substance_formulations?.formulation_name, row.material_profile_snapshot?.profile_name ? "perfil de material" : null, row.material_profile_snapshot?.composition_status === "laboratory_test" ? "composição analisada" : null].filter(Boolean),
  };
}

function symptomEvent(row: any): PsyLogTimelineEvent | null {
  const at = validIso(row.measured_at ?? row.created_at);
  if (!at) return null;
  return {
    id: `symptom:${row.id}`,
    type: "symptom",
    at,
    title: row.symptom_name || row.patient_target_symptoms?.symptom_name || "Sintoma",
    detail: text([row.context, row.notes]),
    intensity: numberValue(row.intensity_0_100 ?? (row.intensity_0_10 != null ? Number(row.intensity_0_10) * 10 : null)),
    source: row,
    linkedDoseId: row.linked_dose_id ?? null,
    tags: ["sintoma", row.symptom_name].filter(Boolean),
  };
}

function checkinEvent(row: any): PsyLogTimelineEvent | null {
  const at = validIso(row.checkin_at ?? row.measured_at ?? row.created_at);
  if (!at) return null;
  const axes = text([
    row.focus_0_100 != null ? `foco ${row.focus_0_100}` : null,
    row.anxiety_0_100 != null ? `ansiedade ${row.anxiety_0_100}` : null,
    row.sedation_0_100 != null ? `sedação ${row.sedation_0_100}` : null,
    row.craving_0_100 != null ? `craving ${row.craving_0_100}` : null,
    row.mood_0_100 != null ? `humor ${row.mood_0_100}` : null,
  ]);
  return {
    id: `checkin:${row.id}`,
    type: "checkin",
    at,
    title: row.title || row.checkin_type || "Check-in",
    detail: text([axes, row.notes, row.context]),
    intensity: numberValue(row.intensity_0_100 ?? row.anxiety_0_100 ?? row.focus_0_100),
    source: row,
    linkedDoseId: row.linked_dose_id ?? null,
    tags: ["check-in", row.checkin_type].filter(Boolean),
  };
}

function sessionEvent(row: any): PsyLogTimelineEvent | null {
  const at = validIso(row.session_at);
  if (!at) return null;
  return {
    id: `session:${row.id}`,
    type: "session",
    at,
    title: row.name || "Sessão clínica",
    detail: text([row.session_type, row.session_status ?? row.status, row.clinical_summary, row.notes]),
    source: row,
    tags: ["sessão", row.session_type, row.session_status ?? row.status].filter(Boolean),
  };
}

function substanceUseEvent(row: any): PsyLogTimelineEvent | null {
  const at = validIso(row.used_at);
  if (!at) return null;
  return {
    id: `substance:${row.id}`,
    type: "substance_use",
    at,
    title: row.substance_name || "Uso não prescrito",
    detail: text([row.amount != null ? `${row.amount} ${row.unit ?? ""}` : null, row.route, row.context, row.trigger, row.consequence, row.physician_notes]),
    intensity: numberValue(row.craving_before ?? row.fissure ?? row.compulsion),
    source: row,
    tags: ["uso não prescrito", row.route, row.intent].filter(Boolean),
  };
}

function genericPsyLogEvent(row: any): PsyLogTimelineEvent | null {
  const at = validIso(row.event_at);
  if (!at) return null;
  return {
    id: `psylog:${row.id}`,
    type: "psylog",
    at,
    title: row.title || row.event_type || "Evento PsyLog",
    detail: row.description || "",
    intensity: numberValue(row.intensity_0_100 ?? (row.intensity_0_10 != null ? Number(row.intensity_0_10) * 10 : null)),
    source: row,
    linkedDoseId: row.linked_dose_id ?? null,
    tags: [row.event_type].filter(Boolean),
  };
}

function vitalEvent(raw: any): PsyLogTimelineEvent | null {
  const row = normalizeVitalRecord(raw);
  const at = validIso(row.measured_at);
  if (!at) return null;
  const alerts = assessVitalRecord(row);
  const urgent = alerts.filter((alert) => alert.level === "urgent").map((alert) => alert.title);
  const attention = alerts.filter((alert) => alert.level === "attention").map((alert) => alert.title);
  return {
    id: `vital:${row.__source_table ?? "vital"}:${row.id ?? at}`,
    type: "vital",
    at,
    title: row.heart_rate_bpm != null ? `FC ${row.heart_rate_bpm} bpm` : "Sinais vitais",
    detail: text([
      row.systolic_mm_hg != null && row.diastolic_mm_hg != null
        ? `PA ${row.systolic_mm_hg}/${row.diastolic_mm_hg}`
        : null,
      row.spo2_percent != null ? `SpO₂ ${row.spo2_percent}%` : null,
      row.respiratory_rate_bpm != null ? `FR ${row.respiratory_rate_bpm}` : null,
      row.temperature_celsius != null ? `${row.temperature_celsius} °C` : null,
      row.weight_kg != null ? `peso ${row.weight_kg} kg` : null,
      row.posture,
      urgent.length ? `alerta: ${urgent.join(", ")}` : null,
      !urgent.length && attention.length ? `atenção: ${attention.join(", ")}` : null,
      row.context,
      row.clinical_note,
    ]),
    source: { ...raw, ...row },
    linkedDoseId: row.linked_dose_id ?? null,
    tags: [
      "sinais vitais",
      row.heart_rate_bpm != null ? "FC" : null,
      row.systolic_mm_hg != null ? "PA" : null,
      row.spo2_percent != null ? "SpO2" : null,
      urgent.length ? "alerta urgente" : attention.length ? "atenção" : null,
      row.__source_table,
    ].filter(Boolean) as string[],
  };
}

export function buildPsyLogTimeline(input: {
  doses?: any[];
  symptomMeasurements?: any[];
  checkins?: any[];
  sessions?: any[];
  substanceUse?: any[];
  psylogEvents?: any[];
  vitalSigns?: any[];
}): PsyLogTimelineEvent[] {
  const doseEvents = (input.doses ?? []).map(doseEvent).filter((event): event is PsyLogTimelineEvent => !!event);
  const mirroredUseKeys = new Set(
    doseEvents
      .filter((event) => /uso_nao_prescrito|recaida|abuso/i.test(String(event.source?.log_type ?? "")))
      .map((event) => `${event.at}|${canonical(event.title)}`),
  );
  const substanceEvents = (input.substanceUse ?? [])
    .map(substanceUseEvent)
    .filter((event): event is PsyLogTimelineEvent => !!event)
    .filter((event) => !mirroredUseKeys.has(`${event.at}|${canonical(event.title)}`));

  const directSourceKeys = new Set<string>([
    ...(input.doses ?? []).map((row) => `medication_dose_logs:${row.id}`),
    ...(input.checkins ?? []).map((row) => `session_checkins:${row.id}`),
    ...(input.vitalSigns ?? []).map((row) => `${row.__source_table ?? "patient_vital_signs"}:${row.id}`),
    ...(input.symptomMeasurements ?? []).map((row) => `symptom_measurements:${row.id}`),
    ...(input.sessions ?? []).map((row) => `clinical_sessions:${row.id}`),
  ]);

  const mirroredEvents = (input.psylogEvents ?? [])
    .filter((row) => {
      const sourceTable = row.source_table;
      const sourceRecordId = row.source_record_id ?? row.source_id;
      if (!sourceTable || !sourceRecordId) return true;
      return !directSourceKeys.has(`${sourceTable}:${sourceRecordId}`);
    })
    .map(genericPsyLogEvent);

  const events = [
    ...doseEvents,
    ...(input.symptomMeasurements ?? []).map(symptomEvent),
    ...(input.checkins ?? []).map(checkinEvent),
    ...(input.sessions ?? []).map(sessionEvent),
    ...substanceEvents,
    ...mirroredEvents,
    ...(input.vitalSigns ?? []).map(vitalEvent),
  ].filter((event): event is PsyLogTimelineEvent => !!event);
  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function groupTimelineByDay(events: PsyLogTimelineEvent[]) {
  const map = new Map<string, PsyLogTimelineEvent[]>();
  for (const event of events) {
    const key = localDateKey(event.at);
    map.set(key, [...(map.get(key) ?? []), event]);
  }
  return [...map.entries()]
    .map(([date, rows]) => ({ date, events: rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()) }))
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function summarizeTimeline(events: PsyLogTimelineEvent[]) {
  const counts = events.reduce<Record<string, number>>((acc, event) => {
    acc[event.type] = (acc[event.type] ?? 0) + 1;
    return acc;
  }, {});
  const redoses = events.filter((event) => event.type === "dose" && /redose|sobreposicao|extra|problem/i.test(String(event.source?.log_type ?? ""))).length;
  const dated = events.length;
  return { counts, redoses, dated, firstAt: events.at(-1)?.at ?? null, lastAt: events[0]?.at ?? null };
}
